/**
 * Every tunable number in the game, in one place.
 *
 * Both the client and the authoritative server import these. If a constant
 * that affects simulation lives anywhere else, the two sides can disagree and
 * the client's prediction will drift against the server for reasons that are
 * very hard to see in a debugger.
 *
 * Units: metres, seconds, radians.
 */

// ---------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------

/** Authoritative simulation + snapshot rate. */
export const TICK_RATE = 20;
export const TICK_MS = 1000 / TICK_RATE;
export const TICK_DT = 1 / TICK_RATE;

/** How often the client flushes an input packet. Matched to the tick rate. */
export const INPUT_SEND_RATE = 20;
export const INPUT_SEND_MS = 1000 / INPUT_SEND_RATE;

/**
 * Number of past input frames repeated in every input packet.
 *
 * Cheap redundancy against loss, and it means the client can sample input at
 * render rate while only paying for 20 packets/sec — which matters because
 * Durable Objects bill inbound WebSocket messages 20:1.
 */
export const INPUT_REDUNDANCY = 3;

/**
 * Remote entities are rendered this far in the past, so there are always two
 * snapshots straddling the render time to interpolate between.
 *
 * Lessons-learned #1: lerping toward the newest packet as it arrives looks
 * fine in a demo and stutters in practice. 2 ticks of buffer at 20Hz.
 */
export const INTERP_DELAY_MS = 100;

/**
 * If the interpolation buffer starves for longer than this, hold the last
 * known pose. Do NOT extrapolate — a wrong guess that has to be visibly
 * corrected reads worse than a brief pause.
 */
export const INTERP_STARVE_MS = 500;

/** Client->server clock sync ping cadence. */
export const PING_INTERVAL_MS = 1000;
/** EMA weight for clock offset. Low = smooth and slow to trust one sample. */
export const CLOCK_EMA_ALPHA = 0.1;
/** EMA weight for round-trip time. Slightly quicker, it is only cosmetic. */
export const RTT_EMA_ALPHA = 0.2;

// ---------------------------------------------------------------------------
// World bounds — these define the quantization range of the wire format.
// Changing them changes the meaning of every position on the wire, so they are
// part of the protocol, not just level data.
// ---------------------------------------------------------------------------

export const WORLD_MIN_XZ = -64;
export const WORLD_MAX_XZ = 64;
export const WORLD_MIN_Y = -8;
export const WORLD_MAX_Y = 56;

// ---------------------------------------------------------------------------
// Movement
// ---------------------------------------------------------------------------

/** Default human hull. Props override this with their own dimensions. */
export const PLAYER_HALF_WIDTH = 0.35;
export const PLAYER_HEIGHT = 1.8;
export const PLAYER_EYE_HEIGHT = 1.65;
export const CROUCH_HEIGHT = 1.1;
export const CROUCH_EYE_HEIGHT = 0.95;

/**
 * Just short of straight up/down, so the view matrix never degenerates and so
 * pitch always survives the wire quantizer's [-PI/2, PI/2] range.
 */
export const PITCH_LIMIT = Math.PI / 2 - 0.01;

export const HUNTER_SPEED = 5.0;
/** Hiders are faster — the classic prop hunt trade for having no weapon. */
export const HIDER_SPEED = 6.0;
export const CROUCH_SPEED_SCALE = 0.45;

/**
 * How close a hider must stand to a prop to take its shape, horizontally.
 *
 * Generous rather than precise. Possession is a deliberate act performed while
 * standing still, not an aimed one, and a tight radius turns it into a game of
 * nudging against a crate looking for the spot where the key works. It is also
 * the radius within which the *nearest* prop wins, so overlapping candidates
 * resolve by distance rather than by making the player step away from one.
 */
export const POSSESS_RANGE = 2.5;

// ---------------------------------------------------------------------------
// Finding a hider
// ---------------------------------------------------------------------------

/**
 * How far a hunter's shot reaches.
 *
 * depot's diagonal is ~98 m and fog far is 130 m, so this is deliberately
 * shorter than "as far as you can see": a hider spotted across the whole level
 * should have to be approached rather than sniped from the spawn.
 */
export const TAG_RANGE = 40;

/**
 * Ticks a hunter must wait after a shot that found somebody.
 *
 * Short. Finding a hider is the reward and there is no reason to make the
 * player wait for it — this exists only so that one press cannot resolve twice.
 */
export const TAG_COOLDOWN_TICKS = Math.round(0.5 * TICK_RATE);

/**
 * Ticks a hunter must wait after a shot that found nothing.
 *
 * This is the rule that makes hiding worth anything. Without a miss penalty the
 * dominant hunter strategy is to shoot every prop on the level — 101 of them on
 * depot — which costs nothing and deletes the entire mechanic. At three seconds
 * apiece, brute-forcing depot takes five minutes against a four-minute round,
 * so searching beats spraying. The exact number is a tuning question to settle
 * by playing; the property that matters is that it exceeds the round.
 */
export const TAG_MISS_PENALTY_TICKS = Math.round(3 * TICK_RATE);

/**
 * How far back the server will rewind the world to resolve a shot.
 *
 * A hunter aims at entities rendered INTERP_DELAY_MS in the past, and the shot
 * then travels for ~RTT/2 before the server sees it, so hit-testing against the
 * present makes a moving hider untouchable at any real latency. The server
 * rewinds to the tick the shooter was actually looking at instead.
 *
 * One second is the bound on that, and it is a bound rather than a courtesy: a
 * client asks to be rewound by naming the newest snapshot it has received, and
 * this clamps how far that request can reach. A dishonest client gains only
 * what an honest client on a bad connection already gets.
 */
export const MAX_REWIND_TICKS = TICK_RATE;

/**
 * How far behind the server's own tick an input frame may claim to be.
 *
 * See `resolveFrameTick`. Matched to MAX_REWIND_TICKS because the same claim
 * feeds both the gate and the rewind, and two windows around one number is one
 * window too many.
 */
export const MAX_INPUT_TICK_LAG = MAX_REWIND_TICKS;

// ---------------------------------------------------------------------------
// Taunts
// ---------------------------------------------------------------------------

/**
 * Bounds on the gap between one taunt and a hider's next.
 *
 * This is the design answer to the problem the static-props-on-the-wire commit
 * solved technically. That one made a still hider byte-identical to scenery;
 * this makes being byte-identical to scenery insufficient, because a hider who
 * finds a corner and never moves still announces themselves every half-minute.
 * Without it, perfect stillness is the dominant hider strategy and the hunter's
 * only counter is to spend the round shooting props — which
 * TAG_MISS_PENALTY_TICKS deliberately made expensive.
 *
 * A range rather than a period, drawn afresh for each player after each taunt,
 * for two reasons. A fixed period makes every hider taunt in unison once the
 * round starts, which is one loud moment and then silence. And it is a
 * metronome: a hunter who hears one taunt knows exactly when the next is due
 * and can be standing there for it. Jitter makes the next one news.
 *
 * Roughly nine taunts per hider over a HUNTING_TICKS round, and the first is
 * drawn from the same range — so a hunter searches for twenty seconds or so
 * before the level starts talking to them.
 */
export const TAUNT_MIN_INTERVAL_TICKS = Math.round(20 * TICK_RATE);
export const TAUNT_MAX_INTERVAL_TICKS = Math.round(35 * TICK_RATE);

/**
 * How far a taunt carries.
 *
 * Shared rather than client-private because **the server culls by it**. A taunt
 * carries a hider's position, which is the one thing the asymmetric snapshot
 * views exist to withhold — see `entityViews` — so broadcasting every taunt to
 * the whole lobby would hand a client that reads its own socket a periodic
 * exact fix on every hider alive, at any range, for the rest of the round.
 * Culling to what a player could actually have heard bounds that to what an
 * honest pair of ears already gets.
 *
 * Slightly beyond TAG_RANGE on purpose: hearing something you cannot yet shoot
 * is the point. A taunt tells a hunter to walk, not to fire.
 */
export const TAUNT_HEARING_RANGE = 45;

export const GROUND_ACCEL = 60;
export const AIR_ACCEL = 12;
export const GROUND_FRICTION = 45;
export const GRAVITY = 22;
export const JUMP_VELOCITY = 7.0;
export const TERMINAL_VELOCITY = 60;

/**
 * How high a ground move may climb without a jump.
 *
 * Collision is axis-aligned boxes with no slope primitive, so every change in
 * ground level is a vertical face. Without this, a 20 cm kerb costs a jump
 * press and a staircase is a parkour course — which is the difference between
 * a level having terrain and a level having obstacles.
 *
 * Half a metre is a stair, not a wall. It is deliberately below the 1.11 m
 * jump apex (JUMP_VELOCITY^2 / 2*GRAVITY), so jumping still reaches somewhere
 * walking cannot, and it is the pitch map elevation is authored in — see
 * `@step` in maptool's ASCII format, where one tier is exactly one step.
 */
export const STEP_HEIGHT = 0.5;

/**
 * Gap left between a hull and a solid after collision resolution.
 *
 * Without it, a resolved contact leaves the hull exactly touching, and the
 * next tick's floating-point comparison can go either way — which shows up as
 * a one-tick jitter that differs between client and server.
 */
export const COLLIDE_EPSILON = 1e-4;

/**
 * Collision resolution only tests the hull's *destination*, so a single tick's
 * displacement must never exceed the thinnest solid in the world or the hull
 * passes clean through it. Movement is sub-stepped to keep each test's
 * displacement under this bound.
 *
 * This is not academic: TERMINAL_VELOCITY is 60 m/s, which is 3 m of travel in
 * one 20Hz tick, against a floor slab 1 m thick.
 *
 * 0.4 m is chosen so that ordinary running (~0.3 m/tick) costs exactly one
 * sub-step and pays nothing for the protection.
 */
export const MAX_SUBSTEP_DISTANCE = 0.4;

/** Hard cap so a pathological velocity cannot stall the tick. */
export const MAX_SUBSTEPS = 16;

/**
 * Map-authoring constraint implied by MAX_SUBSTEP_DISTANCE: no collision solid
 * may be thinner than this on any axis. Enforced by the map validator.
 */
export const MIN_SOLID_THICKNESS = 0.5;

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

/**
 * Positional disagreement below this is ignored.
 *
 * We do not require bit-exact cross-engine determinism (Math.sin/cos are not
 * specified to the last ULP, and a Firefox client talks to a V8 server), so
 * prediction tolerates ULP-scale drift rather than fighting it. See the note
 * in movement.ts.
 */
export const RECONCILE_EPSILON = 0.02;

/** A disagreement this large is a genuine desync: snap instead of easing. */
export const RECONCILE_SNAP_DISTANCE = 2.0;

/**
 * Corrections below the snap distance are eased out over this long, so the
 * camera never jumps. The position is authoritative immediately; only the
 * *rendered* offset decays.
 */
export const RECONCILE_SMOOTH_MS = 100;

/** Client-side ring buffer of unacknowledged inputs. 2s at 20Hz. */
export const INPUT_BUFFER_SIZE = 64;

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export const MAX_PLAYERS = 16;
export const LOBBY_CODE_LENGTH = 5;
/** Ambiguous glyphs (0/O, 1/I/L) removed — these get read aloud over voice. */
export const LOBBY_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const NAME_MAX_LENGTH = 16;

/** Server drops a connection that has sent nothing for this long. */
export const CONNECTION_TIMEOUT_MS = 15_000;
